/**
 * EncounterChargesTab Component
 * 
 * Provides a comprehensive interface for managing encounter charges including:
 * - Auto-detected E&M code with override capability
 * - Procedure charges from orders
 * - Manual charge entry
 * - Charges summary before discharge
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  LocalHospital as HospitalIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getFacilityCPTPrices } from '../../services/facilityPricingService';
import {
  getAvailableEMCodes,
  detectEMCode,
  mapProceduresToCharges
} from '../../services/encounterChargesService';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const EncounterChargesTab = ({
  encounter,
  patient,
  orders = [],
  onChargesChange,
  readOnly = false
}) => {
  const { defaultFacility } = useUserData();
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emCodeOverride, setEmCodeOverride] = useState(null);
  const [manualCharges, setManualCharges] = useState([]);
  const [addChargeDialogOpen, setAddChargeDialogOpen] = useState(false);
  const [emOverrideDialogOpen, setEmOverrideDialogOpen] = useState(false);
  
  // New charge form state
  const [newCharge, setNewCharge] = useState({
    cptCode: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
    diagnosisPointers: [1]
  });
  
  // CPT code search state
  const [cptSearchTerm, setCptSearchTerm] = useState('');
  const [cptSearchResults, setCptSearchResults] = useState([]);
  const [cptSearchLoading, setCptSearchLoading] = useState(false);
  
  // Facility CPT prices state
  const [facilityCptPrices, setFacilityCptPrices] = useState([]);
  const [loadingFacilityPrices, setLoadingFacilityPrices] = useState(false);
  
  const debouncedCptSearch = useDebounce(cptSearchTerm, 300);
  
  // Determine if patient is new (simplified - check recent encounters)
  const isNewPatient = useMemo(() => {
    // For now, assume established patient unless explicitly marked
    return patient?.isNewPatient || false;
  }, [patient]);
  
  // Auto-detect E&M code
  const autoDetectedEM = useMemo(() => {
    if (!encounter) return null;
    return detectEMCode(encounter, isNewPatient);
  }, [encounter, isNewPatient]);
  
  // Get available E&M codes for this encounter type
  const availableEMCodes = useMemo(() => {
    return getAvailableEMCodes(encounter?.type);
  }, [encounter?.type]);
  
  // Current E&M code (override or auto-detected)
  const currentEMCode = useMemo(() => {
    return emCodeOverride || autoDetectedEM;
  }, [emCodeOverride, autoDetectedEM]);
  
  // Map orders to procedure charges
  const procedureCharges = useMemo(() => {
    return mapProceduresToCharges(orders);
  }, [orders]);
  
  // Calculate totals
  const chargesSummary = useMemo(() => {
    let emCharge = 0;
    let proceduresTotal = 0;
    let manualTotal = 0;
    
    // E&M charge (we'd need facility pricing for actual amount)
    if (currentEMCode) {
      emCharge = 150; // Default placeholder - should come from facility pricing
    }
    
    // Procedure charges
    proceduresTotal = procedureCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
    
    // Manual charges
    manualTotal = manualCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
    
    return {
      emCharge,
      proceduresTotal,
      manualTotal,
      grandTotal: emCharge + proceduresTotal + manualTotal,
      itemCount: 1 + procedureCharges.length + manualCharges.length // 1 for E&M
    };
  }, [currentEMCode, procedureCharges, manualCharges]);
  
  // Load facility CPT prices on mount
  useEffect(() => {
    const loadFacilityPrices = async () => {
      if (!defaultFacility?.id) return;
      setLoadingFacilityPrices(true);
      try {
        const prices = await getFacilityCPTPrices(defaultFacility.id);
        setFacilityCptPrices(prices || []);
      } catch (err) {
        console.error('Error loading facility CPT prices:', err);
        setFacilityCptPrices([]);
      } finally {
        setLoadingFacilityPrices(false);
      }
    };
    loadFacilityPrices();
  }, [defaultFacility?.id]);
  
  // Search CPT codes - client-side filtering of facility prices
  useEffect(() => {
    if (!debouncedCptSearch || debouncedCptSearch.length < 2) {
      setCptSearchResults([]);
      return;
    }
    
    const searchLower = debouncedCptSearch.toLowerCase();
    const filtered = facilityCptPrices.filter(price => 
      price.procedureCode?.toLowerCase().includes(searchLower) ||
      price.procedureName?.toLowerCase().includes(searchLower)
    ).slice(0, 20);
    
    setCptSearchResults(filtered);
  }, [debouncedCptSearch, facilityCptPrices]);
  
  // Store callback in ref to avoid dependency issues
  const onChargesChangeRef = useRef(onChargesChange);
  onChargesChangeRef.current = onChargesChange;
  
  // Refs to hold latest values for use in callbacks
  const currentEMCodeRef = useRef(currentEMCode);
  const procedureChargesRef = useRef(procedureCharges);
  const chargesSummaryRef = useRef(chargesSummary);
  
  // Update refs when values change
  currentEMCodeRef.current = currentEMCode;
  procedureChargesRef.current = procedureCharges;
  chargesSummaryRef.current = chargesSummary;
  
  // Helper function to notify parent of charges - called only on user actions
  // Takes the updated state values as parameters to ensure we're using the latest values
  const notifyChargesChange = useCallback((updatedEmCodeOverride, updatedManualCharges) => {
    if (onChargesChangeRef.current) {
      // Calculate the current EM code based on override
      const emCode = updatedEmCodeOverride || autoDetectedEM;
      onChargesChangeRef.current({
        emCode: emCode,
        emCodeOverride: updatedEmCodeOverride,
        procedureCharges: procedureChargesRef.current,
        manualCharges: updatedManualCharges,
        summary: chargesSummaryRef.current
      });
    }
  }, [autoDetectedEM]);
  
  // Handle E&M code override
  const handleEMOverride = (code) => {
    const selectedCode = availableEMCodes.find(c => c.code === code);
    if (selectedCode) {
      const newOverride = {
        code: selectedCode.code,
        description: selectedCode.description,
        modifier: selectedCode.modifier,
        autoDetected: false
      };
      setEmCodeOverride(newOverride);
      // Notify parent with the new values
      notifyChargesChange(newOverride, manualCharges);
    }
    setEmOverrideDialogOpen(false);
  };
  
  // Handle adding manual charge
  const handleAddManualCharge = () => {
    if (!newCharge.cptCode) return;
    
    const charge = {
      id: `manual_${Date.now()}`,
      type: 'manual',
      cptCode: newCharge.cptCode,
      description: newCharge.description,
      quantity: newCharge.quantity || 1,
      unitPrice: newCharge.unitPrice || 0,
      amount: (newCharge.unitPrice || 0) * (newCharge.quantity || 1),
      diagnosisPointers: newCharge.diagnosisPointers || [1],
      addedAt: new Date().toISOString()
    };
    
    const updatedManualCharges = [...manualCharges, charge];
    setManualCharges(updatedManualCharges);
    setNewCharge({
      cptCode: '',
      description: '',
      quantity: 1,
      unitPrice: 0,
      diagnosisPointers: [1]
    });
    setCptSearchTerm('');
    setAddChargeDialogOpen(false);
    // Notify parent with the new values
    notifyChargesChange(emCodeOverride, updatedManualCharges);
  };
  
  // Handle removing manual charge
  const handleRemoveManualCharge = (chargeId) => {
    const updatedManualCharges = manualCharges.filter(c => c.id !== chargeId);
    setManualCharges(updatedManualCharges);
    // Notify parent with the new values
    notifyChargesChange(emCodeOverride, updatedManualCharges);
  };
  
  // Handle CPT code selection
  const handleCptSelect = (cptCode) => {
    if (cptCode) {
      setNewCharge(prev => ({
        ...prev,
        cptCode: cptCode.procedureCode,
        description: cptCode.procedureName || '',
        unitPrice: cptCode.price || 0
      }));
      setCptSearchTerm('');
    }
  };
  
  // Reset E&M override
  const handleResetEMOverride = () => {
    setEmCodeOverride(null);
    // Notify parent with the new values
    notifyChargesChange(null, manualCharges);
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  if (!encounter) {
    return (
      <Alert severity="info">
        Please save the encounter before managing charges.
      </Alert>
    );
  }
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* E&M Code Section */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HospitalIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Evaluation & Management (E&M) Code
              </Typography>
            </Box>
            {!readOnly && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setEmOverrideDialogOpen(true)}
              >
                Override
              </Button>
            )}
          </Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                p: 2, 
                bgcolor: emCodeOverride ? 'warning.light' : 'success.light', 
                borderRadius: 1,
                textAlign: 'center'
              }}>
                <Typography variant="h4" fontWeight="bold" color={emCodeOverride ? 'warning.dark' : 'success.dark'}>
                  {currentEMCode?.code || 'N/A'}
                </Typography>
                <Chip 
                  size="small" 
                  label={emCodeOverride ? 'Manual Override' : 'Auto-Detected'} 
                  color={emCodeOverride ? 'warning' : 'success'}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" gutterBottom>
                {currentEMCode?.description || 'No E&M code selected'}
              </Typography>
              {currentEMCode?.modifier && (
                <Chip label={`Modifier: ${currentEMCode.modifier}`} size="small" sx={{ mr: 1 }} />
              )}
              {autoDetectedEM && !emCodeOverride && (
                <Typography variant="body2" color="text.secondary">
                  <InfoIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                  Based on: {autoDetectedEM.complexity} complexity ({encounter?.type || 'outpatient'} encounter)
                </Typography>
              )}
              {emCodeOverride && (
                <Button 
                  size="small" 
                  onClick={handleResetEMOverride}
                  sx={{ mt: 1 }}
                >
                  Reset to Auto-Detected
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Procedure Charges from Orders */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Procedure Charges from Orders
              </Typography>
              <Chip label={procedureCharges.length} size="small" color="primary" />
            </Box>
          </Box>
          
          {procedureCharges.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />}>
              No procedure orders have been placed for this encounter. Add orders in the "Orders & Procedures" tab.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>CPT Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedureCharges.map((charge) => (
                    <TableRow key={charge.id}>
                      <TableCell>
                        <Chip label={charge.cptCode || 'TBD'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{charge.description}</TableCell>
                      <TableCell align="center">{charge.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(charge.unitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(charge.amount)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={charge.status || 'Pending'} 
                          size="small" 
                          color={charge.status === 'Completed' ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Manual Charges */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Additional Charges
              </Typography>
              <Chip label={manualCharges.length} size="small" color="secondary" />
            </Box>
            {!readOnly && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddChargeDialogOpen(true)}
              >
                Add Charge
              </Button>
            )}
          </Box>
          
          {manualCharges.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />}>
              No additional charges added. Click "Add Charge" to add CPT codes not covered by orders.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>CPT Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                    {!readOnly && <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manualCharges.map((charge) => (
                    <TableRow key={charge.id}>
                      <TableCell>
                        <Chip label={charge.cptCode} size="small" color="secondary" variant="outlined" />
                      </TableCell>
                      <TableCell>{charge.description}</TableCell>
                      <TableCell align="center">{charge.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(charge.unitPrice)}</TableCell>
                      <TableCell align="right">{formatCurrency(charge.amount)}</TableCell>
                      {!readOnly && (
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleRemoveManualCharge(charge.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      
      {/* Charges Summary */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'primary.light' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <MoneyIcon sx={{ color: 'primary.dark' }} />
            <Typography variant="h6" fontWeight="bold" color="primary.dark">
              Charges Summary
            </Typography>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">E&M Charge</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(chargesSummary.emCharge)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Procedures</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(chargesSummary.proceduresTotal)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">Additional</Typography>
                <Typography variant="h6" fontWeight="bold">{formatCurrency(chargesSummary.manualTotal)}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                <Typography variant="body2" color="success.dark">Estimated Total</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.dark">
                  {formatCurrency(chargesSummary.grandTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {chargesSummary.itemCount} line items
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              These charges will be used to generate the bill when the encounter is discharged/completed.
              Actual prices may vary based on facility pricing configuration.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
      
      {/* E&M Override Dialog */}
      <Dialog 
        open={emOverrideDialogOpen} 
        onClose={() => setEmOverrideDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2,
          px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EditIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Override E&M Code
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setEmOverrideDialogOpen(false)} 
            size="small" 
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#fafbfc' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Select an E&M code to override the auto-detected code. The current auto-detected code is: <strong>{autoDetectedEM?.code}</strong>
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Select E&M Code</InputLabel>
            <Select
              value={emCodeOverride?.code || ''}
              label="Select E&M Code"
              onChange={(e) => handleEMOverride(e.target.value)}
              sx={{ backgroundColor: 'white' }}
            >
              {availableEMCodes.map((em) => (
                <MenuItem key={em.code} value={em.code}>
                  <Box>
                    <Typography variant="body1">
                      <strong>{em.code}</strong> - {em.description}
                    </Typography>
                    {em.modifier && (
                      <Chip label={`Modifier: ${em.modifier}`} size="small" sx={{ ml: 1 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setEmOverrideDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Charge Dialog */}
      <Dialog 
        open={addChargeDialogOpen} 
        onClose={() => setAddChargeDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 2,
          px: 3,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add Manual Charge
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setAddChargeDialogOpen(false)} 
            size="small" 
            sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: '#fafbfc' }}>
          <Box sx={{ mt: 1 }}>
            <Autocomplete
              fullWidth
              freeSolo
              options={cptSearchResults}
              loading={loadingFacilityPrices}
              inputValue={cptSearchTerm}
              getOptionLabel={(option) => 
                typeof option === 'string' 
                  ? option 
                  : `${option.procedureCode} - ${option.procedureName}`
              }
              filterOptions={(x) => x} // Disable built-in filtering, using client-side search
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  setCptSearchTerm(value);
                }
              }}
              onChange={(e, value) => handleCptSelect(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search CPT Code"
                  placeholder="Type at least 2 characters to search..."
                  helperText={
                    loadingFacilityPrices 
                      ? "Loading facility prices..." 
                      : cptSearchTerm.length > 0 && cptSearchTerm.length < 2 
                        ? "Type at least 2 characters to search" 
                        : ""
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingFacilityPrices ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box component="li" key={key} {...restProps} sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {option.procedureCode}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.procedureName}
                        </Typography>
                      </Box>
                      {option.price != null && (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                          ${option.price.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              noOptionsText={
                loadingFacilityPrices 
                  ? "Loading facility prices..." 
                  : cptSearchTerm.length < 2 
                    ? "Type to search CPT codes..." 
                    : "No CPT codes found for this facility"
              }
              sx={{ mb: 2.5 }}
            />
            
            <TextField
              fullWidth
              label="CPT Code"
              value={newCharge.cptCode}
              onChange={(e) => setNewCharge(prev => ({ ...prev, cptCode: e.target.value }))}
              sx={{ mb: 2.5, backgroundColor: 'white', borderRadius: 1 }}
              required
              size="medium"
            />
            
            <TextField
              fullWidth
              label="Description"
              value={newCharge.description}
              onChange={(e) => setNewCharge(prev => ({ ...prev, description: e.target.value }))}
              sx={{ mb: 2.5, backgroundColor: 'white', borderRadius: 1 }}
              multiline
              rows={2}
            />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={newCharge.quantity}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  inputProps={{ min: 1 }}
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Unit Price"
                  type="number"
                  value={newCharge.unitPrice}
                  onChange={(e) => setNewCharge(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ backgroundColor: 'white', borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, backgroundColor: '#fafbfc', borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={() => setAddChargeDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddManualCharge}
            disabled={!newCharge.cptCode}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
            }}
          >
            Add Charge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncounterChargesTab;
